import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import moment from "moment";
import DataTable from 'react-data-table-component';
import Footer from "../components/dashboard/footer";
import Header from "../components/dashboard/header";
import Sidebar from "../components/dashboard/sidebar";
import { contentContext } from "../context/contentContext";
import SessionPopup from "../pages/SessionPopup";
import TableLoader from "../components/tableLoader";
import RevenueFilters, { DATE_FORMAT, RevenuePageHeader, TRANSACTION_PRESETS, money, toFilterOptions, useRevenueFilters } from "../components/revenueFilters";
import RefundBookingModal, { remainingOf } from "../components/stripe/RefundBookingModal";
import RefundHistory, { refundsOf } from "../components/stripe/RefundHistory";
import DisputeDetails, { DISPUTE_OPEN, disputeNoteOf, hasDisputeDetails } from "../components/stripe/DisputeDetails";
import { MUTED } from "../components/stripe/stripeStatusMeta";
import { canRefund } from "../utils/adminRoles";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

let { lambda, appname } = window.app

// ── Transaction list helpers ─────────────────────────────────────────────────
const refundedLabel = (row) => {
    // A refund stamp that names another PaymentIntent came from the refund flow's
    // updateMany, not from a refund of this charge (same guard as the backend report).
    const refundPi = row?.refundStatus?.payment_intent;
    const ownPi = row?.paymentIntentId || row?.paymentResult?.id;
    return row?.refundPayment && (!refundPi || !ownPi || refundPi === ownPi);
};

const statusLabel = (row) => {
    if (DISPUTE_OPEN.includes(row?.disputeStatus)) return "Disputed";
    if (row?.disputeStatus === "lost") return "Dispute Lost";
    if (refundedLabel(row)) return row?.refundPayment === "partial" ? "Partially Refunded" : "Refunded";
    const s = String(row?.status || "").toLowerCase();
    if (["success", "succeeded", "paid", "active"].includes(s)) return "Paid";
    if (s === "refunded") return "Refunded";
    if (s === "refunddue") return "Refund Due";
    if (s === "failed") return "Failed";
    if (s.includes("cancel")) return "Canceled";
    return "Incomplete";
};

const STATUS_BADGE = {
    Paid: "bg-success",
    Refunded: "bg-danger",
    "Partially Refunded": "bg-info text-dark",
    Disputed: "bg-warning text-dark",
    "Dispute Lost": "bg-danger",
    "Refund Due": "bg-warning text-dark",
    Failed: "bg-secondary",
    Canceled: "bg-secondary",
    Incomplete: "bg-light text-dark",
};

// A visitor payment an admin can still refund: a real Stripe payment, paid or partly
// refunded, with money left on it. The API checks all of this again with Stripe.
const refundable = (row) =>
    !!(row?.paymentResult?.id || row?.paymentIntentId)
    && ["Paid", "Partially Refunded"].includes(statusLabel(row))
    && remainingOf(row) > 0;

// A visitor payment with refunds to show under its row. A refunded row from before
// refunds[] existed still opens, to say there is no note.
const hasRefundHistory = (row) =>
    refundsOf(row).length > 0 || ["Refunded", "Partially Refunded"].includes(statusLabel(row));

// What opens under a visitor row: its dispute, then its refunds.
const RowDetails = ({ data: row }) => {
    const refunds = hasRefundHistory(row);
    return (
        <>
            {hasDisputeDetails(row) && <DisputeDetails data={row} last={!refunds} />}
            {refunds && <RefundHistory data={row} />}
        </>
    );
};

const Transactions = () => {
    const navigate = useNavigate();
    const { setRoute, setActiveMenuId, userData } = useContext(contentContext);
    // Refunds are for Admins and Financial Managers only; anyone else sees the list without the button.
    const mayRefund = canRefund(userData?.userType);
    const [showSessionPopupup, setShowSessionPopupup] = useState(false);

    // Starts at All Time so the page opens on every transaction, as it always has.
    const filters = useRevenueFilters("allTime");
    const { dateFrom, dateTo, client, location } = filters;
    const [filterOptions, setFilterOptions] = useState({ clients: [], locations: [] });

    const [activeTab, setActiveTab] = useState("clients");
    const [transactionsData, setTransactionsData] = useState({ transactions: [], visirorTransactions: [] });
    const [listLoading, setListLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [refundRow, setRefundRow] = useState(null);
    // transactionIds of the visitor rows whose dispute and refunds are open.
    const [openRefunds, setOpenRefunds] = useState(() => new Set());

    useEffect(() => {
        if (!localStorage.getItem("token")) {
            navigate("/");
        }
        setRoute("client")
        setActiveMenuId(200006)
        userActivity();
        getTransactionData();
        getFilterOptions();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const userActivity = () => {
        let path = window.location.pathname.split("/");
        const pageName = path[path.length - 1];
        const payload = {
            "userid": localStorage.getItem("userId"),
            "pagename": pageName,
            "pageurl": window.location.href,
            "starttime": moment(),
            "useragent": JSON.parse(localStorage.getItem("loc"))
        };
        const previousId = localStorage.getItem("previousid");
        const urlLink = lambda + '/useractivity?appname=' + appname + (previousId ? "&previousid=" + previousId : "");
        axios({ method: 'POST', url: urlLink, data: payload })
            .then(function (response) {
                if (response?.data?.statusCode === 200) {
                    localStorage.setItem("previousid", response?.data?.result)
                }
            });
    }

    const getTransactionData = () => {
        setListLoading(true);
        const token = localStorage.getItem("token");
        axios({
            method: 'POST',
            url: lambda + '/allTransactions?appname=' + appname + "&token=" + token,
        })
            .then(function (response) {
                if (response?.data?.result === "Invalid token or Expired") {
                    setShowSessionPopupup(true);
                } else {
                    setTransactionsData({
                        transactions: response?.data?.result?.transactions || [],
                        visirorTransactions: response?.data?.result?.visirorTransactions || [],
                    });
                }
            })
            .catch((err) => console.log('Error:', err))
            .finally(() => setListLoading(false));
    };

    // Client and location names for the dropdowns and the Client/Location columns.
    const getFilterOptions = () => {
        const token = localStorage.getItem("token");
        axios({ method: 'POST', url: `${lambda}/revenueReport?appname=${appname}&token=${token}&optionsOnly=true` })
            .then((response) => {
                const result = response?.data?.result;
                if (result === "Invalid token or Expired") {
                    setShowSessionPopupup(true);
                    return;
                }
                setFilterOptions(toFilterOptions(result?.options));
            })
            .catch((err) => console.log('Error:', err));
    };

    const storeMap = useMemo(() => {
        const m = {};
        filterOptions.locations.forEach((l) => { m[l.value] = l; });
        return m;
    }, [filterOptions]);

    const clientNameMap = useMemo(() => {
        const m = {};
        filterOptions.clients.forEach((c) => { m[c.value] = c.label; });
        return m;
    }, [filterOptions]);

    const inRange = (created) => {
        if (!created) return false;
        const day = moment(created).format(DATE_FORMAT);
        return day >= dateFrom && day <= dateTo;
    };

    const matchesSearch = (values) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return values.some((v) => String(v ?? "").toLowerCase().includes(q));
    };

    const clientRows = useMemo(() => {
        // Plans belong to a client, not a location, so none match a location filter.
        if (location) return [];
        return (transactionsData.transactions || []).filter((t) =>
            inRange(t.created)
            && (!client || t.clientid === client.value)
            && matchesSearch([t.transactionId, t.address?.firstName, t.address?.lastName, t.address?.email, t.planType, clientNameMap[t.clientid]]));
    }, [transactionsData, dateFrom, dateTo, client, location, search, clientNameMap]); // eslint-disable-line react-hooks/exhaustive-deps

    const visitorRows = useMemo(() => {
        return (transactionsData.visirorTransactions || []).filter((t) =>
            inRange(t.created)
            && (!location || t.storeid === location.value)
            && (!client || storeMap[t.storeid]?.clientid === client.value)
            && matchesSearch([t.transactionId, t.plan, storeMap[t.storeid]?.label, storeMap[t.storeid]?.clientName]));
    }, [transactionsData, dateFrom, dateTo, client, location, search, storeMap]); // eslint-disable-line react-hooks/exhaustive-deps

    // The badge, plus a grey line under it when a dispute ended without being lost: the
    // badge keeps saying where the money is (Paid, Refunded), the line says it was disputed.
    const statusCell = (row) => {
        const label = statusLabel(row);
        const badge = <span className={`badge ${STATUS_BADGE[label] || "bg-light text-dark"}`}>{label}</span>;
        const note = disputeNoteOf(row);
        if (!note) return badge;
        return (
            <div className="d-flex flex-column align-items-start">
                {badge}
                <span style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{note}</span>
            </div>
        );
    };

    const toggleRefunds = (transactionId) => {
        setOpenRefunds((prev) => {
            const next = new Set(prev);
            if (next.has(transactionId)) next.delete(transactionId);
            else next.add(transactionId);
            return next;
        });
    };

    // The status badge, plus a toggle for the dispute and refunds under the row. Only the
    // Visitors table opens rows, so the Clients table keeps the plain badge.
    const visitorStatusCell = (row) => {
        const disputed = hasDisputeDetails(row);
        const refunded = hasRefundHistory(row);
        if (!disputed && !refunded) return statusCell(row);
        const open = openRefunds.has(row.transactionId);
        const count = refundsOf(row).length;
        const closedLabel = disputed && refunded ? "View details"
            : disputed ? "View dispute"
                : count > 1 ? "View notes" : "View note";
        return (
            <div className="d-flex flex-column align-items-start">
                {statusCell(row)}
                <button
                    type="button"
                    className="btn btn-link p-0 mt-1 d-inline-flex align-items-center"
                    id={`refund-notes-btn-${row.transactionId}`}
                    aria-expanded={open}
                    style={{ fontSize: 12, textDecoration: "none" }}
                    onClick={() => toggleRefunds(row.transactionId)}
                >
                    {open ? "Hide" : closedLabel}
                    <span className="material-icons" style={{ fontSize: 16 }}>{open ? "expand_less" : "expand_more"}</span>
                </button>
            </div>
        );
    };

    const clientsColumns = [
        { name: 'Transaction Id', selector: row => row?.transactionId ?? "", sortable: true, minWidth: "220px" },
        { name: 'Date', selector: row => row?.created ?? "", cell: row => row?.created ? moment(row.created).format("MM-DD-YYYY") : "", sortable: true },
        { name: 'Client', selector: row => clientNameMap[row?.clientid] ?? "", sortable: true },
        { name: 'First Name', selector: row => row?.address?.firstName ?? "", sortable: true },
        { name: 'Last Name', selector: row => row?.address?.lastName ?? "", sortable: true },
        { name: 'EmailId', selector: row => row?.address?.email ?? "", sortable: true, minWidth: "200px" },
        { name: 'Plan Type', selector: row => row?.planType ?? "", sortable: true },
        { name: 'Status', selector: row => statusLabel(row), cell: statusCell, sortable: true },
        { name: 'Grand Total', selector: row => Number(row?.grandTotal) || 0, cell: row => money(row?.grandTotal), sortable: true },
    ];

    const visitorsColumns = [
        { name: 'Transaction Id', selector: row => row?.transactionId ?? "", sortable: true, minWidth: "220px" },
        { name: 'Date', selector: row => row?.created ?? "", cell: row => row?.created ? moment(row.created).format("MM-DD-YYYY") : "", sortable: true },
        { name: 'Location', selector: row => storeMap[row?.storeid]?.label ?? "", sortable: true },
        { name: 'Client', selector: row => storeMap[row?.storeid]?.clientName ?? "", sortable: true },
        { name: 'Plan', selector: row => row?.plan ?? "", sortable: true },
        { name: 'Status', selector: row => statusLabel(row), cell: visitorStatusCell, sortable: true },
        { name: 'Total Amount', selector: row => Number(row?.totalAmount) || 0, cell: row => money(row?.totalAmount), sortable: true },
        { name: 'Client Share', selector: row => Number(row?.clientPayoutAmount) || 0, cell: row => row?.clientPayoutAmount != null ? money(row.clientPayoutAmount) : "—", sortable: true },
        { name: 'SpotSaver Commission', selector: row => Number(row?.commissionAmount) || 0, cell: row => row?.commissionAmount != null ? money(row.commissionAmount) : "—", sortable: true },
        {
            name: 'Actions',
            omit: !mayRefund,
            ignoreRowClick: true,
            button: true,
            minWidth: "110px",
            cell: row => mayRefund && refundable(row) ? (
                <button
                    type="button"
                    className="stroke-btn-style"
                    id={`refund-btn-${row.transactionId}`}
                    style={{ textTransform: "none", padding: "4px 14px", fontSize: 13 }}
                    onClick={() => setRefundRow(row)}
                >
                    Refund
                </button>
            ) : null,
        },
    ];

    const onRefunded = (result) => {
        setRefundRow(null);
        const venuePart = result?.venueShareReversed > 0 ? ` ${money(result.venueShareReversed)} taken back from the venue.` : "";
        const cancelled = result?.bookingCancelled ? " Booking cancelled." : "";
        toast.success(`Refunded ${money(result?.refunded)}.${venuePart}${cancelled}`);
        getTransactionData();
    };

    const customNoRecords = (message) => (
        <div className="empty-state-body empty-record">
            <div className="empty-state__message">
                <span className="material-icons">summarize</span>
                <p className="form-check font-size-16">{message}</p>
            </div>
        </div>
    );

    return (
        <>
            {showSessionPopupup && <SessionPopup />}
            {refundRow && (
                <RefundBookingModal
                    row={refundRow}
                    locationName={storeMap[refundRow.storeid]?.label}
                    clientName={storeMap[refundRow.storeid]?.clientName}
                    onClose={() => setRefundRow(null)}
                    onRefunded={onRefunded}
                    onSessionExpired={() => { setRefundRow(null); setShowSessionPopupup(true); }}
                />
            )}
            <div id="layout-wrapper">
                <Header breadcrumb={["Finance", "Transactions"]} />
                <Sidebar />

                <div className="main-content user-management transaction-page revenue-scope">
                    <div className="page-content">
                        <div className="container-fluid">

                            <RevenuePageHeader
                                title="Transactions"
                                onReset={() => { filters.reset(); setSearch(""); }}
                            />

                            <RevenueFilters
                                filters={filters}
                                clientOptions={filterOptions.clients}
                                locationOptions={filterOptions.locations}
                                presets={TRANSACTION_PRESETS}
                            />

                            <div className="row table-data">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body edit-store">
                                            <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                                                <li className="nav-item" role="presentation">
                                                    <a
                                                        id="transaction-clients"
                                                        className={`nav-link ${activeTab === 'clients' ? 'active' : ''}`}
                                                        onClick={() => setActiveTab("clients")}
                                                        role="tab"
                                                    >
                                                        <span className="d-none d-sm-block">CLIENTS ({clientRows.length})</span>
                                                    </a>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <a
                                                        id="transaction-visitors"
                                                        className={`nav-link ${activeTab === 'visitors' ? 'active' : ''}`}
                                                        onClick={() => setActiveTab("visitors")}
                                                        role="tab"
                                                    >
                                                        <span className="d-none d-sm-block">VISITORS ({visitorRows.length})</span>
                                                    </a>
                                                </li>
                                            </ul>

                                            <div className="tab-content p-3 text-muted">
                                                <div className="tab-pane active show add-client-details" role="tabpanel">
                                                    <div className="row mb-2" id="table-header-row">
                                                        <div className="col-sm-4 button-block" id="button-block"></div>
                                                        <div className="col-sm-8">
                                                            <div className="search-box mb-2 d-inline-block" id="search-box">
                                                                <div className="position-relative">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder={activeTab === "clients" ? "Search by Name, Email or Transaction ID" : "Search by Plan, Location or Transaction ID"}
                                                                        value={search}
                                                                        onChange={(e) => setSearch(e.target.value)}
                                                                        id={activeTab === "clients" ? "client-input-search" : "visitor-input-search"}
                                                                    />
                                                                    <span className="material-icons search-icon revenue-search-icon">search</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <DataTable
                                                        id="categoriesTable"
                                                        key={activeTab}
                                                        columns={activeTab === "clients" ? clientsColumns : visitorsColumns}
                                                        keyField="_id"
                                                        data={activeTab === "clients" ? clientRows : visitorRows}
                                                        direction="auto"
                                                        highlightOnHover
                                                        pagination
                                                        responsive
                                                        persistTableHead
                                                        striped
                                                        noDataComponent={customNoRecords(activeTab === "clients" && location
                                                            ? "Plan subscriptions are not tied to a location"
                                                            : "No transactions were found")}
                                                        paginationPerPage={15}
                                                        paginationRowsPerPageOptions={[15, 25, 50, 75, 100]}
                                                        progressPending={listLoading}
                                                        progressComponent={<TableLoader />}
                                                        expandableRows={activeTab === "visitors"}
                                                        expandableRowsHideExpander
                                                        expandableRowExpanded={(row) => openRefunds.has(row.transactionId)}
                                                        expandableRowsComponent={RowDetails}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Footer />
                </div>
            </div>
            <ToastContainer position="bottom-center" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover={false} theme="light" />
        </>
    );
};

export default Transactions;
